/**
 * CertificationsSection Component
 * Hanterar behörigheter/certifieringar för byggföretag
 */

import React, { useState } from 'react';
import { CERTIFICATION_OPTIONS, getCertificationInfo } from '../../../constants/certificationTypes';
import { API_URL, authHeaders } from '../../../utils/api';
import CertifieringsMarke from './CertifieringsMarke';
import RegisterLankFalt from './RegisterLankFalt';

// Hjälpfunktion för att fixa UTF-8 encoding
const fixEncoding = (str) => {
  if (!str) return str;
  try {
    // Försök dekoda om det ser ut som felaktig UTF-8
    if (str.includes('Ã') || str.includes('Ã¤') || str.includes('Ã¶')) {
      // Konvertera från felaktigt tolkad Latin-1 till UTF-8
      const bytes = new Uint8Array([...str].map(c => c.charCodeAt(0)));
      return new TextDecoder('utf-8').decode(bytes);
    }
    return str;
  } catch {
    return str;
  }
};

export const CertificationsSection = ({ certifications = [], onChange, builderId }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [newCert, setNewCert] = useState({
    type: '',
    name: '',
    certificateNumber: '',
    expiryDate: '',
    registerUrl: '',
    registerForetag: '',
    registerOrgnr: ''
  });
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [generatingAppendix, setGeneratingAppendix] = useState(false);

  // Lägg till ny behörighet
  const handleAddCertification = () => {
    if (!newCert.type) return;

    const certInfo = getCertificationInfo(newCert.type);
    const certification = {
      type: newCert.type,
      name: newCert.type === 'other' ? newCert.name : certInfo.name,
      certificateNumber: newCert.certificateNumber,
      expiryDate: newCert.expiryDate || null,
      documentUrl: null,
      documentName: null,
      registerUrl: newCert.registerUrl.trim() || null,
      registerForetag: newCert.registerForetag.trim() || null,
      registerOrgnr: newCert.registerOrgnr.trim() || null,
      isActive: true,
      addedAt: new Date().toISOString()
    };

    onChange([...certifications, certification]);
    setNewCert({ type: '', name: '', certificateNumber: '', expiryDate: '', registerUrl: '', registerForetag: '', registerOrgnr: '' });
    setShowAddForm(false);
  };

  // Ta bort behörighet
  const handleRemoveCertification = (index) => {
    const updated = certifications.filter((_, i) => i !== index);
    onChange(updated);
    setDeleteConfirmIndex(null);
  };

  // Hantera filuppladdning
  const handleFileUpload = async (index, file) => {
    if (!file) return;

    setUploadingIndex(index);

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('certificationIndex', index);

      // Skapa temporär URL för preview (ersätts av riktig URL efter upload)
      const tempUrl = URL.createObjectURL(file);

      const updated = [...certifications];
      updated[index] = {
        ...updated[index],
        documentUrl: tempUrl,
        documentName: file.name,
        _pendingFile: file // Markera att filen behöver laddas upp
      };
      onChange(updated);

    } catch (error) {
      console.error('Fel vid uppladdning:', error);
      alert('Kunde inte ladda upp dokumentet');
    } finally {
      setUploadingIndex(null);
    }
  };

  // Ta bort dokument
  const handleRemoveDocument = (index) => {
    const updated = [...certifications];
    updated[index] = {
      ...updated[index],
      documentUrl: null,
      documentName: null,
      _pendingFile: null
    };
    onChange(updated);
  };

  // Registeruppgifter: företag, org.nr och länk (se RegisterLankFalt). Backend
  // normaliserar länken och kastar allt som inte är http(s).
  const handleRegisterChange = (index, andringar) => {
    const updated = [...certifications];
    updated[index] = { ...updated[index], ...andringar };
    onChange(updated);
  };

  // Formattera datum
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  // Kolla om ett utgångsdatum har passerat
  const isExpired = (dateString) => {
    if (!dateString) return false;
    return new Date(dateString) < new Date();
  };

  // Kolla om det finns dokument att slå ihop
  // Registerlänkar räknas — bilagan gör en standardsida av dem
  const hasDocuments = certifications.some(cert =>
    (cert.documentUrl && !cert.documentUrl.startsWith('blob:')) || cert.registerUrl
  );

  // Generera behörighetsbilaga
  const handleGenerateAppendix = async () => {
    if (!builderId) {
      alert('Spara först byggföretaget innan du kan generera bilagan');
      return;
    }

    setGeneratingAppendix(true);
    try {
      const response = await fetch(`${API_URL}/builders/${builderId}/certifications-appendix`, { headers: authHeaders() });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Kunde inte generera bilagan');
      }

      // Hämta filnamn från Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = 'Behörighetsbilaga.pdf';
      if (contentDisposition) {
        const filenameStarMatch = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/);
        const filenameMatch = contentDisposition.match(/filename="(.+?)"/);
        if (filenameStarMatch) {
          fileName = decodeURIComponent(filenameStarMatch[1]);
        } else if (filenameMatch) {
          fileName = filenameMatch[1];
        }
      }

      // Ladda ner filen
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Fel vid generering av bilaga:', error);
      alert(error.message || 'Kunde inte generera behörighetsbilagan');
    } finally {
      setGeneratingAppendix(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Lista över befintliga behörigheter - kompakt med expandering */}
      {certifications.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
          {certifications.map((cert, index) => {
            const certInfo = getCertificationInfo(cert.type);
            const expired = isExpired(cert.expiryDate);
            const isExpanded = expandedIndex === index;
            const hasDocument = !!cert.documentUrl;
            const displayName = fixEncoding(cert.name) || certInfo.name;
            const documentName = fixEncoding(cert.documentName) || 'Dokument';

            return (
              <div key={index} className={expired ? 'bg-red-50' : 'bg-white'}>
                {/* Kompakt rad - alltid synlig */}
                <div
                  className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedIndex(isExpanded ? null : index)}
                >
                  <CertifieringsMarke type={cert.type} size="sm" />

                  {/* Namn och status */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 text-sm truncate">
                        {displayName}
                      </span>
                      {expired && (
                        <span className="text-xs text-red-600 bg-red-100 px-1.5 py-0.5 rounded">Utgången</span>
                      )}
                      {hasDocument && (
                        <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {cert.registerUrl && (
                        <svg className="w-4 h-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-label="Registerlänk">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      {cert.certificateNumber && <span>Nr: {cert.certificateNumber}</span>}
                      {cert.expiryDate && (
                        <span className={expired ? 'text-red-600' : ''}>
                          Utgår: {formatDate(cert.expiryDate)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Expand/collapse ikon */}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                {/* Expanderat innehåll */}
                {isExpanded && (
                  <div className="px-3 pb-3 pt-1 bg-gray-50 border-t border-gray-100">
                    <p className="text-xs text-gray-500 mb-2">{certInfo.description}</p>

                    {/* Registerlänk — för behörigheter som bara finns som en
                        sida hos utfärdaren och inte som PDF */}
                    <div className="mb-3">
                      <RegisterLankFalt
                        type={cert.type}
                        value={cert}
                        onChange={(andringar) => handleRegisterChange(index, andringar)}
                      />
                    </div>

                    {/* Dokumenthantering */}
                    <div className="flex items-center justify-between">
                      {cert.documentUrl ? (
                        <div className="flex items-center gap-2 flex-1">
                          <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-sm text-gray-700 truncate flex-1">
                            {documentName}
                          </span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!cert._pendingFile && cert.documentUrl && !cert.documentUrl.startsWith('blob:') && (
                              <a
                                href={cert.documentUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Visa
                              </a>
                            )}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); handleRemoveDocument(index); }}
                              className="text-red-600 hover:text-red-800 text-xs"
                            >
                              Ta bort fil
                            </button>
                          </div>
                        </div>
                      ) : (
                        <label className="flex items-center gap-2 px-3 py-1.5 border border-dashed border-gray-300 rounded cursor-pointer hover:border-leadme-pink hover:bg-pink-50 transition-colors text-sm">
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={(e) => { e.stopPropagation(); handleFileUpload(index, e.target.files[0]); }}
                            disabled={uploadingIndex === index}
                          />
                          {uploadingIndex === index ? (
                            <span className="text-gray-500">Laddar upp...</span>
                          ) : (
                            <>
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                              </svg>
                              <span className="text-gray-500">Ladda upp intyg</span>
                            </>
                          )}
                        </label>
                      )}

                      {/* Ta bort behörighet */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setDeleteConfirmIndex(index); }}
                        className="ml-3 text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Ta bort
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Lägg till ny behörighet */}
      {showAddForm ? (
        <div className="border border-leadme-pink rounded-lg p-4 bg-pink-50">
          <h4 className="font-medium text-gray-900 mb-3">Lägg till behörighet</h4>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ av behörighet *
              </label>
              <select
                value={newCert.type}
                onChange={(e) => setNewCert({ ...newCert, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
              >
                <option value="">Välj behörighet...</option>
                {CERTIFICATION_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {newCert.type === 'other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Namn på behörigheten *
                </label>
                <input
                  type="text"
                  value={newCert.name}
                  onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
                  placeholder="T.ex. Heta arbeten"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Certifikatnummer
                </label>
                <input
                  type="text"
                  value={newCert.certificateNumber}
                  onChange={(e) => setNewCert({ ...newCert, certificateNumber: e.target.value })}
                  placeholder="Valfritt"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Utgångsdatum
                </label>
                <input
                  type="date"
                  value={newCert.expiryDate}
                  onChange={(e) => setNewCert({ ...newCert, expiryDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-leadme-pink focus:border-transparent"
                />
              </div>
            </div>

            {newCert.type && (
              <RegisterLankFalt
                key={newCert.type}
                type={newCert.type}
                value={newCert}
                onChange={(andringar) => setNewCert(prev => ({ ...prev, ...andringar }))}
              />
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewCert({ type: '', name: '', certificateNumber: '', expiryDate: '', registerUrl: '', registerForetag: '', registerOrgnr: '' });
                }}
                className="flex-1 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleAddCertification}
                disabled={!newCert.type || (newCert.type === 'other' && !newCert.name)}
                className="flex-1 px-4 py-2 bg-leadme-pink text-white rounded-lg hover:bg-leadme-pink-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Lägg till
              </button>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-leadme-pink hover:text-leadme-pink hover:bg-pink-50 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Lägg till behörighet
        </button>
      )}

      {certifications.length === 0 && !showAddForm && (
        <p className="text-sm text-gray-500 text-center py-4">
          Inga behörigheter tillagda. Klicka på knappen ovan för att lägga till.
        </p>
      )}

      {/* Bekräftelse-modal för borttagning */}
      {deleteConfirmIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Bekräfta borttagning
            </h3>
            <p className="text-gray-600 mb-2">
              Är du säker på att du vill ta bort denna behörighet?
            </p>
            <p className="text-sm font-medium text-gray-900 mb-6">
              {certifications[deleteConfirmIndex]?.name || 'Okänd behörighet'}
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setDeleteConfirmIndex(null)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={() => handleRemoveCertification(deleteConfirmIndex)}
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Ta bort
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Knapp för att generera behörighetsbilaga */}
      {hasDocuments && (
        <div className="pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleGenerateAppendix}
            disabled={generatingAppendix}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors"
          >
            {generatingAppendix ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Genererar bilaga...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Skapa behörighetsbilaga
              </>
            )}
          </button>
          <p className="text-xs text-gray-500 text-center mt-2">
            Samlar uppladdade dokument och registerlänkar i en PDF
          </p>
        </div>
      )}
    </div>
  );
};

export default CertificationsSection;
